import { useMemo } from 'react';

import { useQuery } from '@mint-lib/query-client';
import { Auth } from '@mint-sdk/api-auth';
import { UsersTags } from '@mint-sdk/api-tags';

type UserLabel = Auth.UserItem['tags'][number];

export type UserTagsById = ReadonlyMap<number, UserLabel[]>;

const EMPTY_TAGS: UserTagsById = new Map();

const toUserLabel = ({
  id,
  name,
  category,
  level,
  hierarchyName,
}: UsersTags.UserTag): UserLabel => ({
  id,
  name,
  category,
  level,
  categoryType: hierarchyName,
});

const toUniqueLabels = (tags: UsersTags.UserTag[]): UserLabel[] => {
  const byId = new Map<string, UserLabel>();

  for (const tag of tags) {
    if (!byId.has(tag.id)) {
      byId.set(tag.id, toUserLabel(tag));
    }
  }

  return [...byId.values()];
};

export const useUsersTags = (enabled = true) => {
  const query = useQuery({
    ...UsersTags.usersTagsQueryOptions({}),
    refetchOnWindowFocus: false,
    enabled,
  });

  const userTagsById = useMemo<UserTagsById>(() => {
    if (!query.data) {
      return EMPTY_TAGS;
    }

    return new Map(
      query.data.map(({ userId, tags }) => [userId, toUniqueLabels(tags)]),
    );
  }, [query.data]);

  return {
    userTagsById,
    // Rows render before the labels arrive... the Labels column shows its own loading state until then.
    isUserTagsLoading: (query.isPending || query.isFetching) && enabled,
    refetchUserTags: query.refetch,
  };
};
